export interface Fragment {
  text: string;
  startMs: number;
  endMs: number;
}

export interface Exchange {
  user: Fragment[];
  reply: Fragment[];
}

export type HandledBy = "gpt-live" | "xenia";

export interface ReplyMark {
  handledBy: HandledBy;
  narration: boolean;
}

const BACKCHANNEL_MAX_WORDS = 3;

export function appendDelta(text: string, fragment: Fragment): string {
  return /[.!?,;:]$/.test(text) && /^[^\s.!?,;:]/.test(fragment.text) ? `${text} ${fragment.text}` : text + fragment.text;
}

export function joinFragments(fragments: Fragment[]): string {
  return fragments.reduce(appendDelta, "").replace(/\s+/g, " ").trim();
}

export const isRealReply = (text: string): boolean =>
  text.endsWith("?") || text.split(" ").filter(Boolean).length > BACKCHANNEL_MAX_WORDS;

export function isBackchannel(reply: Fragment[], next: Fragment, quietMs: number): boolean {
  if (reply.length === 0) return false;
  const replyEnd = Math.max(...reply.map((fragment) => fragment.endMs));
  return !isRealReply(joinFragments(reply)) && next.startMs - replyEnd < quietMs;
}

export class LiveTranscript {
  private user: Fragment[] = [];
  private reply: Fragment[] = [];
  private held: Fragment[] = [];
  private busyLine: Exchange | null = null;
  private heardMs = -1;
  private narrating = false;
  private sayWindow = false;
  private quietMs: number;

  constructor(quietMs: number) {
    this.quietMs = quietMs;
  }

  pendingText(): string {
    return joinFragments(this.user);
  }

  pendingFragments(): Fragment[] {
    return this.user;
  }

  hasReply(): boolean {
    return this.reply.length > 0;
  }

  replyFragments(): Fragment[] {
    return this.reply;
  }

  hasRealReply(): boolean {
    return isRealReply(joinFragments(this.reply));
  }

  addUser(fragment: Fragment, holding = false): Exchange | null {
    this.heardMs = Math.max(this.heardMs, fragment.endMs);
    this.narrating = false;
    this.sayWindow = false;
    let closed: Exchange | null = null;
    if (this.busyLine && this.busyLine.reply.length > 0) {
      closed = this.busyLine;
      this.busyLine = null;
    }
    if (this.reply.length > 0 && !holding) {
      if (isBackchannel(this.reply, fragment, this.quietMs)) this.held = this.reply;
      else {
        closed = { user: this.user, reply: this.reply };
        this.user = [];
        this.held = [];
      }
      this.reply = [];
    }
    this.user.push(fragment);
    return closed;
  }

  addReply(fragment: Fragment): ReplyMark {
    if (this.busyLine) {
      this.busyLine.reply.push(fragment);
      return { handledBy: "gpt-live", narration: false };
    }
    if (this.sayWindow) return { handledBy: "xenia", narration: true };
    if (this.narrating) return { handledBy: "gpt-live", narration: true };
    const heldEnd = this.held.length > 0 ? Math.max(...this.held.map((held) => held.endMs)) : null;
    if (this.reply.length === 0 && heldEnd !== null && fragment.startMs - heldEnd < this.quietMs) this.reply = this.held;
    this.held = [];
    this.reply.push(fragment);
    return { handledBy: "gpt-live", narration: false };
  }

  markSaySent(): void {
    this.sayWindow = true;
  }

  resumeNarration(): boolean {
    if (this.narrating || this.sayWindow || this.busyLine || this.user.length > 0 || this.reply.length > 0) return false;
    this.narrating = true;
    return true;
  }

  handOffReady(offsetMs: number): boolean {
    return this.heardMs >= offsetMs || joinFragments(this.user.filter((fragment) => fragment.startMs <= offsetMs)) !== "";
  }

  holdBusy(offsetMs: number): Fragment[] {
    const user = this.user.filter((fragment) => fragment.startMs <= offsetMs);
    if (user.length === 0) return user;
    const rest = this.user.filter((fragment) => fragment.startMs > offsetMs);
    while (rest.length > 0 && rest[0].startMs - user[user.length - 1].endMs <= this.quietMs) user.push(rest.shift() as Fragment);
    this.user = rest;
    const line = this.busyLine ?? { user: [], reply: [] };
    line.user.push(...user);
    line.reply.push(...this.reply);
    this.reply = [];
    this.held = [];
    this.busyLine = line;
    return user;
  }

  joinBusy(fragment: Fragment): boolean {
    if (!this.busyLine) return false;
    this.busyLine.user.push(fragment);
    this.heardMs = Math.max(this.heardMs, fragment.endMs);
    return true;
  }

  awaitsBusyReply(): boolean {
    return !!this.busyLine && this.busyLine.reply.length === 0;
  }

  settleBusy(): Exchange | null {
    if (!this.busyLine || this.busyLine.reply.length === 0) return null;
    const exchange = this.busyLine;
    this.busyLine = null;
    return exchange;
  }

  reopen(user: Fragment[]): void {
    this.user = [...user, ...this.user];
  }

  takeHandOff(offsetMs: number): Exchange {
    const user = this.user.filter((fragment) => fragment.startMs <= offsetMs);
    this.user = this.user.filter((fragment) => fragment.startMs > offsetMs);
    const exchange = { user, reply: this.reply };
    this.reply = [];
    this.held = [];
    this.narrating = true;
    return exchange;
  }

  settle(): Exchange | null {
    if (this.reply.length === 0) return null;
    const exchange = { user: this.user, reply: this.reply };
    this.user = [];
    this.reply = [];
    this.held = [];
    return exchange;
  }

  drain(): Exchange {
    const exchange = { user: this.user, reply: this.reply };
    this.user = [];
    this.reply = [];
    this.held = [];
    return exchange;
  }
}
